import React, { useEffect, useState, useCallback } from 'react';
import { BookOpen, Users, ShoppingCart, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardHome = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState({
    totalBooks: 0,
    activeBooks: 0,
    totalUsers: 0,
    totalOrders: 0
  });

  const loadStats = useCallback(async () => {
    try {
      const [booksRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/books?active_only=false`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/admin/users`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);

      setStats({
        totalBooks: booksRes.data.length,
        activeBooks: booksRes.data.filter(b => b.isActive !== false).length,
        totalUsers: usersRes.data.length,
        totalOrders: 0
      });
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const statCards = [
    { title: 'Total Books', value: stats.totalBooks, icon: BookOpen, color: 'blue' },
    { title: 'Active Books', value: stats.activeBooks, icon: Activity, color: 'green' },
    { title: 'Users', value: stats.totalUsers, icon: Users, color: 'purple' },
    { title: 'Orders', value: stats.totalOrders, icon: ShoppingCart, color: 'orange' }
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard Overview</h1>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="border-l-4 border-l-blue-600">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <Icon className={`h-5 w-5 text-${stat.color}-600`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-800">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <a href="/admin/books" className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                <BookOpen className="h-8 w-8 text-blue-600 mb-2" />
                <h3 className="font-semibold">Manage Books</h3>
                <p className="text-sm text-gray-600">Add, edit, or deactivate books</p>
              </a>
              <a href="/admin/users" className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
                <Users className="h-8 w-8 text-purple-600 mb-2" />
                <h3 className="font-semibold">Manage Users</h3>
                <p className="text-sm text-gray-600">Add or remove editors</p>
              </a>
              <a href="/books" className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                <Activity className="h-8 w-8 text-green-600 mb-2" />
                <h3 className="font-semibold">View Site</h3>
                <p className="text-sm text-gray-600">Preview the public site</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;